'use client';
import { type UserCredentialsCode } from '@capsicum/common';
import { useRouter } from '@capsicum/common-web';
import { RequirementsPageCredentialsNeeded, useCredentials, useTrackRequirementsEvents } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';

import { useDeniedRequirementsModal } from '../../regulations/hooks/useDeniedRequirementsModal';
import { deniedCredentialAtom, guardedRouteAtom } from '../data-access';

import { useGenericDeniedCredentialModal } from './useGenericDeniedCredentialModal';

export type CredentialHandlerParams = {
	credentialCodes: Array<UserCredentialsCode> | UserCredentialsCode;
	onCredentialGranted?: () => void;
	onDismiss?: () => void;
	page?: string;
};

const getRequirementsPage = (pathname: string): RequirementsPageCredentialsNeeded | string => {
	const RouteToRequirementPage: Record<string, RequirementsPageCredentialsNeeded> = {
		'/nc-wallet/topup': RequirementsPageCredentialsNeeded.ncWalletTopup,
		'/wallet/receive': RequirementsPageCredentialsNeeded.walletReceive,
		'/wallet/topup': RequirementsPageCredentialsNeeded.walletTopUp,
		'/wallet/withdraw': RequirementsPageCredentialsNeeded.walletWithdraw
	};

	return RouteToRequirementPage[pathname] || pathname;
};

/**
 * Hook that returns the status for a given credential and the action to perfom in case it's denied.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {UserCredentialsCode|UserCredentialsCode[]} params.credentialCodes - Refers to either a unique credential code or an array of credential codes to check.
 * @param {Function} params.onDismiss - Action to perform when dismissing a non requirement reason.
 * @param {Function} params.onCredentialGranted - Action to perform once the credential has been granted.
 *
 * @returns {Object} An object containing the following properties:
 * - `status`: Current credential status.
 * - `onDenied`: Function to execute based on the denied reason.
 */
export const useCredentialsBaseHandler = ({
	credentialCodes,
	onCredentialGranted,
	onDismiss,
	page
}: CredentialHandlerParams) => {
	const router = useRouter();

	const setDeniedCredential = useSetAtom(deniedCredentialAtom);
	const setGuardedRoute = useSetAtom(guardedRouteAtom);

	const { deniedReason, deniedRequirements, firstMissingRequirement, status } = useCredentials(credentialCodes);
	const { trackRequirementsNeededReason } = useTrackRequirementsEvents();

	const deniedCredential = Array.isArray(credentialCodes) ? credentialCodes[0] : credentialCodes;
	const { showGenericDeniedModal } = useGenericDeniedCredentialModal({ deniedCredential, onDismiss });
	const { showDeniedRequirementModal } = useDeniedRequirementsModal({ onDismiss });

	const onDenied = () => {
		switch (deniedReason) {
			case 'GENERIC_DENIED_REASON':
				showGenericDeniedModal();
				break;
			case 'REQUIREMENT_DENIED_REASON':
				showDeniedRequirementModal(deniedRequirements);
				trackRequirementsNeededReason({
					page: page || getRequirementsPage(router.pathname),
					reason: deniedReason,
					requirements: deniedRequirements
				});
				break;
			case 'REQUIREMENT_NOT_COMPLETED_REASON':
				setDeniedCredential({ credentialCodes, onCredentialGranted });
				trackRequirementsNeededReason({
					page: page || getRequirementsPage(router.pathname),
					reason: deniedReason,
					requirements: firstMissingRequirement
				});

				if (!onCredentialGranted) {
					setGuardedRoute({ href: router.pathname + window.location?.search });
					router.replace('/requirements');
				} else {
					router.push('/requirements');
				}
				break;
		}
	};

	return {
		onDenied,
		status
	};
};
